import React, { useCallback, useMemo } from 'react';
import type { CapitalNeed, CapitalNeedInput, UpdateCapitalNeedInput } from '#/graphql/types';
import Form, { FormAction } from '#/common/components/Form';
import { useNavigate } from 'react-router-dom';
import useToast from '#/common/hooks/useToast';
import useUpdateCapitalNeedMutation from '../../hooks/useUpdateCapitalNeedMutation';
import { get, isNil, omitBy } from 'lodash';
import useCreateCapitalNeedMutation from '../../hooks/useCreateCapitalNeedMutation';
import useDeleteCapitalNeedMutation from '../../hooks/useDeleteCapitalNeedMutation';
import allFields from '../../utils/fields';
import { getCleanFormErrors } from '#/common/utils/customRelatedItemsValidation';
import { createFields, updateFields } from './fields';
import useAccessControl from '#/common/hooks/useAccessControl';

export interface CapitalNeedFormProps {
  capitalNeed: CapitalNeed;
  title: string;
}

const toDateOnlyString = (value: unknown): unknown => {
  if (value instanceof Date) return value.toISOString().split('T')[0];
  if (typeof value === 'string' && value) {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) return parsed.toISOString().split('T')[0];
  }
  return value;
};

const CapitalNeedForm: React.FC<CapitalNeedFormProps> = ({ capitalNeed, title }) => {
  const { alert: showAlert, confirm } = useToast();
  const navigate = useNavigate();
  const { canCreateCapitalNeed, canUpdateCapitalNeed, canDeleteCapitalNeed } = useAccessControl();
  const id = get(capitalNeed, 'id', undefined);
  const canSubmit = id ? canUpdateCapitalNeed : canCreateCapitalNeed;

  const { updateCapitalNeed } = useUpdateCapitalNeedMutation();
  const { createCapitalNeed } = useCreateCapitalNeedMutation();
  const { deleteCapitalNeed } = useDeleteCapitalNeedMutation();

  const sourceFields = id ? updateFields : createFields;

  const submit = useCallback(
    (data: CapitalNeed) => {
      const {
        id,
        number,
        createdAt,
        createdBy,
        updatedAt,
        updatedBy,
        displayNumber,
        __typename,
        ...filteredData
      } = data;

      const payload = {
        ...filteredData,
        plannedDate: toDateOnlyString(filteredData.plannedDate),
        cost: filteredData.cost == null ? undefined : Number(filteredData.cost),
      };

      if (!id) {
        return createCapitalNeed(omitBy(payload, isNil) as CapitalNeedInput);
      }
      return updateCapitalNeed(id, omitBy(payload, isNil) as UpdateCapitalNeedInput);
    },
    [id, updateCapitalNeed, createCapitalNeed],
  );

  const handleSubmitError = useCallback(
    (error: any) => {
      if (error.message) {
        showAlert('error', error.message);
      } else {
        showAlert('error', 'An error occurred while saving');
      }
    },
    [showAlert],
  );

  const actions = useMemo(() => {
    const result: FormAction<CapitalNeed>[] = [];

    if (id && canDeleteCapitalNeed) {
      result.push({
        label: 'Delete',
        icon: 'trash',
        type: 'danger',
        onClick: async (e) => {
          const sure = await confirm('error', 'Are you sure you want to delete this capital need?');
          if (!sure) return;

          e.setIsLoading(true);
          await deleteCapitalNeed(id, capitalNeed.assetId ?? undefined);
          e.setIsLoading(false);
          navigate(
            capitalNeed.assetId ? `/assets/${capitalNeed.assetId}/capital-needs` : '/capital-needs',
          );
        },
      });
    }

    if (submit && canSubmit) {
      result.push({
        label: 'Submit',
        type: 'submit',
        onClick: async (e) => {
          if (!e.record) return;

          const validation = e.form?.validate();
          if (
            validation &&
            !validation.isValid &&
            getCleanFormErrors(validation, allFields, e.record).length
          ) {
            showAlert('error', `${title} is not valid, check field entries.`);
            return;
          }

          try {
            e.setIsLoading(true);
            const result = (await submit(e.record)) as any;
            e.setIsLoading(false);

            if (result?.errors) {
              showAlert('error', result.errors.join(';\n'));
              return;
            }
            if (!id && result?.data?.id) {
              navigate(`/capital-needs/${result.data.id}/details`, { replace: true });
            }

            e.clearDirty();
            showAlert('success', 'Changes saved!');
          } catch (error: any) {
            e.setIsLoading(false);
            handleSubmitError(error);
          }
        },
      });
    }

    return result;
  }, [
    id,
    navigate,
    submit,
    confirm,
    showAlert,
    handleSubmitError,
    title,
    canDeleteCapitalNeed,
    canCreateCapitalNeed,
    canUpdateCapitalNeed,
  ]);

  return (
    <Form<CapitalNeed>
      key={id ? `capital-need-form-${id}` : 'capital-need-form-new'}
      record={capitalNeed}
      fields={sourceFields}
      actions={actions}
      supportTenant
      readonly={!canSubmit}
    />
  );
};

export default React.memo(CapitalNeedForm);
